import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const TOTAL_TRADES_REQUIRED = 30;
const FUTURES_SYMBOLS = ["ES", "NQ", "CL", "GC", "SI", "ZB", "ZN", "RTY", "YM", "6E", "6J"];

export default function QuarterToClose() {
  const { user } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTradeModal, setShowNewTradeModal] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [activeTab, setActiveTab] = useState("challenge");
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [formData, setFormData] = useState({
    symbol: "ES",
    direction: "LONG",
    entry_price: "",
    exit_price: "",
    followed_rules: true,
    outcome: "win",
    notes: "",
  });

  useEffect(() => {
    fetchChallenge();
    fetchLeaderboard();
    fetchMyStats();
  }, []);

  const fetchChallenge = async () => {
    try {
      const res = await axios.get(`${API}/quarter-to-close`);
      setChallenge(res.data.challenge);
      setTrades(res.data.trades || []);
    } catch (err) {
      console.error("Failed to fetch challenge:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/quarter-to-close/leaderboard`);
      setLeaderboard(res.data);
    } catch (err) {
      console.error("Failed to fetch leaderboard:", err);
    }
  };

  const fetchMyStats = async () => {
    try {
      const res = await axios.get(`${API}/quarter-to-close/my-stats`);
      setMyStats(res.data);
    } catch (err) {
      console.error("Failed to fetch my stats:", err);
    }
  };

  const startChallenge = async () => {
    try {
      const res = await axios.post(`${API}/quarter-to-close/start`);
      setChallenge(res.data);
      setTrades([]);
      setShowStartModal(false);
      toast.success("Quarter to Close challenge started! Execute 30 disciplined trades.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start challenge");
    }
  };

  const resetChallenge = async () => {
    if (!window.confirm("Reset your challenge? All progress will be lost.")) return;
    try {
      await axios.delete(`${API}/quarter-to-close/reset`);
      setChallenge(null);
      setTrades([]);
      toast.success("Challenge reset. Start fresh when you're ready.");
    } catch (err) {
      toast.error("Failed to reset challenge");
    }
  };

  const logTrade = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        entry_price: parseFloat(formData.entry_price),
        exit_price: parseFloat(formData.exit_price),
      };
      const res = await axios.post(`${API}/quarter-to-close/trade`, payload);
      setChallenge(res.data.challenge);
      setTrades(res.data.trades);
      setShowNewTradeModal(false);
      setFormData({
        symbol: "ES",
        direction: "LONG",
        entry_price: "",
        exit_price: "",
        followed_rules: true,
        outcome: "win",
        notes: "",
      });
      
      if (res.data.challenge.completed) {
        toast.success("🎉 CONGRATULATIONS! You've completed the Quarter to Close challenge!");
      } else if (formData.followed_rules) {
        toast.success(`Trade ${res.data.trades.length}/30 logged! Keep executing with discipline.`);
      } else {
        toast.error("Trade logged, but you broke your rules. This resets your streak!");
      }
      fetchMyStats();
      fetchLeaderboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log trade");
    }
  };

  const completedTrades = trades.filter(t => t.followed_rules).length;
  const brokenRules = trades.filter(t => !t.followed_rules).length;
  const progress = challenge ? (completedTrades / TOTAL_TRADES_REQUIRED) * 100 : 0;
  const currentStreak = challenge?.current_streak || 0;
  const bestStreak = challenge?.best_streak || 0;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="font-mono text-blue-500 animate-pulse">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="quarter-to-close-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-bold text-white">QUARTER TO CLOSE</h1>
          <p className="text-sm text-slate-500 font-mono">30 consecutive disciplined trades. No exceptions.</p>
        </div>
        {challenge && !challenge.completed && (
          <button
            onClick={() => setShowNewTradeModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="log-trade-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            LOG TRADE
          </button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0A0A0A] border border-[#262626] p-1">
          <TabsTrigger value="challenge" className="font-mono text-sm data-[state=active]:bg-[#171717]">
            MY CHALLENGE
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="font-mono text-sm data-[state=active]:bg-[#171717]">
            LEADERBOARD
            {leaderboard.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-500 rounded">
                {leaderboard.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Challenge Tab */}
        <TabsContent value="challenge" className="space-y-6 mt-6">
      {!challenge && (
        <div className="card-institutional p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 border-2 border-blue-500/30 bg-blue-500/10 flex items-center justify-center">
            <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h2 className="text-xl font-mono font-bold text-white mb-4">THE QUARTER TO CLOSE CHALLENGE</h2>
          <p className="text-slate-400 max-w-lg mx-auto mb-6">
            Execute <span className="text-blue-500 font-bold">30 consecutive trades</span> following your rules perfectly. 
            Don't judge by P&L—judge by execution. This is how you forge discipline and eliminate fear.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
            <div className="bg-[#050505] border border-[#262626] p-4">
              <p className="text-3xl font-mono font-bold text-blue-500 mb-1">30</p>
              <p className="text-xs text-slate-500">TRADES REQUIRED</p>
            </div>
            <div className="bg-[#050505] border border-[#262626] p-4">
              <p className="text-3xl font-mono font-bold text-emerald-500 mb-1">100%</p>
              <p className="text-xs text-slate-500">RULES FOLLOWED</p>
            </div>
            <div className="bg-[#050505] border border-[#262626] p-4">
              <p className="text-3xl font-mono font-bold text-yellow-500 mb-1">0</p>
              <p className="text-xs text-slate-500">TOLERANCE FOR EXCUSES</p>
            </div>
          </div>

          <button
            onClick={() => setShowStartModal(true)}
            className="btn-primary px-8 py-4 text-lg animate-pulse-glow"
            data-testid="start-challenge-btn"
          >
            BEGIN THE CHALLENGE
          </button>
        </div>
      )}

      {/* Active Challenge */}
      {challenge && !challenge.completed && (
        <>
          {/* Progress Section */}
          <div className="card-institutional p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-mono text-slate-500">PROGRESS</p>
                <p className="text-3xl font-mono font-bold text-white">
                  {completedTrades} <span className="text-slate-500">/ {TOTAL_TRADES_REQUIRED}</span>
                </p>
              </div>
              <div className={`px-4 py-2 border ${
                progress >= 100 ? "border-emerald-500 bg-emerald-500/10 text-emerald-500" :
                progress >= 50 ? "border-blue-500 bg-blue-500/10 text-blue-500" :
                "border-yellow-500 bg-yellow-500/10 text-yellow-500"
              }`}>
                <span className="font-mono">{Math.round(progress)}% COMPLETE</span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-4 bg-[#050505] border border-[#262626] mb-4">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>

            {/* Trade Dots */}
            <div className="flex flex-wrap gap-2 mb-4">
              {Array.from({ length: TOTAL_TRADES_REQUIRED }).map((_, i) => {
                const trade = trades[i];
                return (
                  <div
                    key={i}
                    className={`w-6 h-6 border flex items-center justify-center text-xs font-mono ${
                      trade
                        ? trade.followed_rules
                          ? "border-emerald-500 bg-emerald-500/20 text-emerald-500"
                          : "border-red-500 bg-red-500/20 text-red-500"
                        : "border-[#262626] text-slate-600"
                    }`}
                    title={trade ? (trade.followed_rules ? "Rules Followed" : "Rules Broken") : `Trade ${i + 1}`}
                  >
                    {trade ? (trade.followed_rules ? "✓" : "✗") : i + 1}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card-institutional p-4">
              <p className="text-xs font-mono text-slate-500 mb-1">CURRENT STREAK</p>
              <p className="text-2xl font-mono font-bold text-blue-500">{currentStreak}</p>
            </div>
            <div className="card-institutional p-4">
              <p className="text-xs font-mono text-slate-500 mb-1">BEST STREAK</p>
              <p className="text-2xl font-mono font-bold text-emerald-500">{bestStreak}</p>
            </div>
            <div className="card-institutional p-4">
              <p className="text-xs font-mono text-slate-500 mb-1">DISCIPLINED</p>
              <p className="text-2xl font-mono font-bold text-emerald-500">{completedTrades}</p>
            </div>
            <div className="card-institutional p-4 border-red-500/20">
              <p className="text-xs font-mono text-red-500 mb-1">RULES BROKEN</p>
              <p className="text-2xl font-mono font-bold text-red-500">{brokenRules}</p>
            </div>
          </div>

          {/* Recent Trades */}
          {trades.length > 0 && (
            <div className="card-institutional">
              <div className="p-4 border-b border-[#262626]">
                <h3 className="font-mono text-sm font-bold text-white">CHALLENGE TRADES</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#262626]">
                      {["#", "SYMBOL", "DIR", "ENTRY", "EXIT", "OUTCOME", "RULES", "DATE"].map(h => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-mono text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {trades.slice().reverse().map((trade, i) => (
                      <tr key={trade.id} className="border-b border-[#262626]">
                        <td className="px-4 py-2 text-sm font-mono text-slate-400">{trades.length - i}</td>
                        <td className="px-4 py-2 text-sm font-mono text-white font-bold">{trade.symbol}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs font-mono px-2 py-1 ${
                            trade.direction === "LONG" ? "bg-emerald-500/20 text-emerald-500" : "bg-red-500/20 text-red-500"
                          }`}>{trade.direction}</span>
                        </td>
                        <td className="px-4 py-2 text-sm font-mono text-slate-300">{trade.entry_price}</td>
                        <td className="px-4 py-2 text-sm font-mono text-slate-300">{trade.exit_price}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs font-mono ${
                            trade.outcome === "win" ? "text-emerald-500" : "text-red-500"
                          }`}>{trade.outcome?.toUpperCase()}</span>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`text-xs font-mono px-2 py-1 border ${
                            trade.followed_rules
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
                              : "border-red-500/30 bg-red-500/10 text-red-500"
                          }`}>{trade.followed_rules ? "YES" : "NO"}</span>
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-slate-500">
                          {new Date(trade.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Reset Button */}
          <div className="text-center">
            <button
              onClick={resetChallenge}
              className="text-xs font-mono text-slate-500 hover:text-red-500 transition-colors"
            >
              RESET CHALLENGE
            </button>
          </div>
        </>
      )}

      {/* Challenge Completed */}
      {challenge?.completed && (
        <div className="card-institutional p-8 text-center border-emerald-500/50">
          <div className="w-24 h-24 mx-auto mb-6 border-2 border-emerald-500 bg-emerald-500/10 flex items-center justify-center animate-pulse-glow">
            <svg className="w-12 h-12 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-mono font-bold text-emerald-500 mb-4">CHALLENGE COMPLETE!</h2>
          <p className="text-slate-400 max-w-lg mx-auto mb-6">
            You've executed {TOTAL_TRADES_REQUIRED} disciplined trades. You've proven you can follow your rules 
            without hesitation or fear. This is the mindset of a professional trader.
          </p>
          
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
            <div className="bg-[#050505] border border-emerald-500/30 p-4">
              <p className="text-2xl font-mono font-bold text-emerald-500">{completedTrades}</p>
              <p className="text-xs text-slate-500">DISCIPLINED</p>
            </div>
            <div className="bg-[#050505] border border-emerald-500/30 p-4">
              <p className="text-2xl font-mono font-bold text-emerald-500">{bestStreak}</p>
              <p className="text-xs text-slate-500">BEST STREAK</p>
            </div>
            <div className="bg-[#050505] border border-[#262626] p-4">
              <p className="text-2xl font-mono font-bold text-red-500">{brokenRules}</p>
              <p className="text-xs text-slate-500">RULES BROKEN</p>
            </div>
          </div>

          <button
            onClick={resetChallenge}
            className="btn-secondary px-6"
          >
            START NEW CHALLENGE
          </button>
        </div>
      )}
      </TabsContent>

      {/* Leaderboard Tab */}
      <TabsContent value="leaderboard" className="space-y-6 mt-6">
        {/* My Stats Card */}
        {myStats && myStats.challenges_completed > 0 && (
          <div className="card-institutional p-6 border-blue-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-sm font-bold text-white">YOUR STATS</h3>
              {myStats.rank && (
                <div className="flex items-center gap-2 px-3 py-1 bg-yellow-500/10 border border-yellow-500/30">
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="font-mono text-yellow-500">RANK #{myStats.rank}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-emerald-500">{myStats.challenges_completed}</p>
                <p className="text-xs text-slate-500">COMPLETED</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-blue-500">{myStats.best_streak}</p>
                <p className="text-xs text-slate-500">BEST STREAK</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-white">{myStats.total_disciplined}</p>
                <p className="text-xs text-slate-500">DISCIPLINED</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-yellow-500">{myStats.discipline_rate}%</p>
                <p className="text-xs text-slate-500">DISCIPLINE</p>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Table */}
        <div className="card-institutional overflow-hidden">
          <div className="p-4 border-b border-[#262626] flex items-center gap-3">
            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
            </svg>
            <h3 className="font-mono text-sm font-bold text-white">QUARTER TO CLOSE LEADERBOARD</h3>
          </div>
          
          {leaderboard.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="w-12 h-12 mx-auto text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-slate-500 font-mono">No completed challenges yet</p>
              <p className="text-slate-600 text-sm mt-2">Be the first to complete the Quarter to Close challenge!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#262626]">
                    {["RANK", "TRADER", "CHALLENGES", "BEST STREAK", "DISCIPLINE", "LAST COMPLETED"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-mono text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry) => (
                    <tr key={entry.rank} className={`border-b border-[#262626] ${
                      entry.name === user?.name ? "bg-blue-500/5" : "hover:bg-[#171717]"
                    }`}>
                      <td className="px-4 py-3">
                        {entry.rank <= 3 ? (
                          <div className={`w-8 h-8 flex items-center justify-center font-mono font-bold ${
                            entry.rank === 1 ? "bg-yellow-500/20 text-yellow-500 border border-yellow-500/30" :
                            entry.rank === 2 ? "bg-slate-400/20 text-slate-400 border border-slate-400/30" :
                            "bg-orange-500/20 text-orange-500 border border-orange-500/30"
                          }`}>
                            {entry.rank}
                          </div>
                        ) : (
                          <span className="text-slate-400 font-mono">{entry.rank}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                            <span className="text-blue-500 font-mono text-sm">{entry.name?.[0]?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-mono text-white text-sm">{entry.name}</p>
                            <p className="text-xs text-slate-600">{entry.email_hint}</p>
                          </div>
                          {entry.name === user?.name && (
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-500 font-mono">YOU</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-emerald-500 font-bold">{entry.challenges_completed}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-blue-500">{entry.best_streak}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-mono ${
                          entry.discipline_rate >= 90 ? "text-emerald-500" :
                          entry.discipline_rate >= 70 ? "text-yellow-500" :
                          "text-red-500"
                        }`}>{entry.discipline_rate}%</span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-500">
                        {entry.last_completed ? new Date(entry.last_completed).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Leaderboard Info */}
        <div className="bg-yellow-500/5 border border-yellow-500/20 p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-mono text-sm text-yellow-500 font-bold mb-1">HOW TO CLIMB THE RANKS</p>
              <p className="text-sm text-slate-400">
                Complete more Quarter to Close challenges, maintain high streaks, and keep your discipline rate above 90%. 
                The leaderboard ranks traders by challenges completed, then by best streak.
              </p>
            </div>
          </div>
        </div>
      </TabsContent>
      </Tabs>

      {/* Quote */}
      <div className="bg-blue-500/5 border border-blue-500/20 p-4 text-center">
        <p className="text-sm text-blue-500/80 italic">
          "Execute 30 identical trades following your rules perfectly. Don't judge by P&L—judge by execution. 
          This is how you trade without fear or hesitation."
        </p>
        <p className="text-xs text-blue-500/60 mt-2">— Quarter to Close Methodology</p>
      </div>

      {/* Start Challenge Modal */}
      <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono text-white">BEGIN QUARTER TO CLOSE</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-400 text-sm">
              You're about to begin the <span className="text-blue-500">Quarter to Close</span> challenge. 
              Your mission: execute 30 trades following your rules <span className="text-white font-bold">perfectly</span>.
            </p>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-4">
              <p className="text-yellow-500 text-sm font-mono mb-2">THE RULES:</p>
              <ul className="text-slate-400 text-sm space-y-1">
                <li>• Log every trade honestly</li>
                <li>• Mark if you followed your rules</li>
                <li>• P&L doesn't matter—execution does</li>
                <li>• Complete 30 disciplined trades</li>
              </ul>
            </div>
            
            <div className="flex gap-4">
              <button onClick={() => setShowStartModal(false)} className="flex-1 btn-secondary">
                NOT YET
              </button>
              <button onClick={startChallenge} className="flex-1 btn-primary">
                I'M READY
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Trade Modal */}
      <Dialog open={showNewTradeModal} onOpenChange={setShowNewTradeModal}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono text-white">LOG CHALLENGE TRADE #{trades.length + 1}</DialogTitle>
          </DialogHeader>
          <form onSubmit={logTrade} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-2">SYMBOL</label>
                <Select value={formData.symbol} onValueChange={(v) => setFormData({ ...formData, symbol: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#262626] font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                    {FUTURES_SYMBOLS.map((s) => (
                      <SelectItem key={s} value={s} className="font-mono">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-2">DIRECTION</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, direction: "LONG" })}
                    className={`flex-1 py-2 font-mono text-xs border ${
                      formData.direction === "LONG"
                        ? "bg-emerald-500/20 border-emerald-500 text-emerald-500"
                        : "border-[#262626] text-slate-400"
                    }`}
                  >LONG</button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, direction: "SHORT" })}
                    className={`flex-1 py-2 font-mono text-xs border ${
                      formData.direction === "SHORT"
                        ? "bg-red-500/20 border-red-500 text-red-500"
                        : "border-[#262626] text-slate-400"
                    }`}
                  >SHORT</button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-2">ENTRY PRICE</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.entry_price}
                  onChange={(e) => setFormData({ ...formData, entry_price: e.target.value })}
                  className="w-full input-institutional px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-2">EXIT PRICE</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.exit_price}
                  onChange={(e) => setFormData({ ...formData, exit_price: e.target.value })}
                  className="w-full input-institutional px-3 py-2"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono text-slate-500 mb-2">OUTCOME</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, outcome: "win" })}
                  className={`flex-1 py-2 font-mono text-xs border ${
                    formData.outcome === "win"
                      ? "bg-emerald-500/20 border-emerald-500 text-emerald-500"
                      : "border-[#262626] text-slate-400"
                  }`}
                >WIN</button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, outcome: "loss" })}
                  className={`flex-1 py-2 font-mono text-xs border ${
                    formData.outcome === "loss"
                      ? "bg-red-500/20 border-red-500 text-red-500"
                      : "border-[#262626] text-slate-400"
                  }`}
                >LOSS</button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, outcome: "breakeven" })}
                  className={`flex-1 py-2 font-mono text-xs border ${
                    formData.outcome === "breakeven"
                      ? "bg-slate-500/20 border-slate-500 text-slate-400"
                      : "border-[#262626] text-slate-400"
                  }`}
                >B/E</button>
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 p-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="followed_rules"
                  checked={formData.followed_rules}
                  onCheckedChange={(checked) => setFormData({ ...formData, followed_rules: checked })}
                  className="border-yellow-500 data-[state=checked]:bg-emerald-500"
                />
                <label htmlFor="followed_rules" className="text-sm text-white font-bold">
                  I FOLLOWED MY RULES ON THIS TRADE
                </label>
              </div>
              <p className="text-xs text-yellow-500/70 mt-2">
                Be honest. The only person you're cheating is yourself.
              </p>
            </div>

            <div>
              <label className="block text-xs font-mono text-slate-500 mb-2">NOTES (OPTIONAL)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full input-institutional px-3 py-2 h-16 resize-none"
                placeholder="What did you learn from this trade?"
              />
            </div>

            <button type="submit" className="w-full btn-primary py-3">
              LOG TRADE #{trades.length + 1}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
